import { approvalPlugins } from "../approvals/registry";

export const APPROVES_PAGE_PATH = "/permission-approves";

const isPrivileged = (user) =>
  !!user && (user.role === "admin" || !!user.fullAccess);

const tokens = (user) =>
  Array.isArray(user?.approvalAccess) ? user.approvalAccess : [];

const hasExplicit = (user) => tokens(user).length > 0;

const allEntityTypes = () => Object.keys(approvalPlugins);

const hasApprovesPage = (user) => {
  const pages = user?.pages || [];
  return pages.some(
    (t) =>
      t === APPROVES_PAGE_PATH ||
      t === `${APPROVES_PAGE_PATH}:write` ||
      t.split(":")[0] === APPROVES_PAGE_PATH,
  );
};

/** Entity types the user may view in the approval inbox. */
export const getReadableApprovalEntityTypes = (user) => {
  if (isPrivileged(user)) return allEntityTypes();
  if (!hasApprovesPage(user)) return [];
  // Any approval-access grant unlocks all registered modules in Tür / inbox
  // (profiles created before "sale" only had order tokens).
  if (!hasExplicit(user)) return [];
  return allEntityTypes();
};

export const hasApprovalEntityRead = (user, entityType) =>
  getReadableApprovalEntityTypes(user).includes(entityType);

export const hasApprovalEntityWrite = (user, entityType) => {
  if (isPrivileged(user)) return true;
  if (!hasApprovesPage(user)) return false;
  const t = tokens(user);
  if (t.includes(`${entityType}:write`)) return true;
  // Legacy: a single :write token predating multi-type still allows actions.
  return t.some((tok) => tok.endsWith(":write"));
};

export const canActOnApprovalItem = (user, item) =>
  hasApprovalEntityWrite(user, item?.type || item?.entityType);

/**
 * Read access gates the entity type. Beyond that, only *pending* workflow steps
 * are narrowed to their assigned approvers — resolved records stay visible so
 * history and "all" listings work for every reader.
 */
export const filterItemsByApprovalAccess = (user, items) => {
  const allowed = new Set(getReadableApprovalEntityTypes(user));
  if (!items?.length) return [];
  return items.filter((item) => {
    if (!allowed.has(item?.type || item?.entityType)) return false;
    if (isPrivileged(user)) return true;
    if (item?.status !== "pending") return true;
    if (!item?.workflowStepKey || item?.stage !== "admin") return true;
    return (item.approverUserIds || []).some(
      (id) => String(id) === String(user?._id || user?.id),
    );
  });
};
